//@flow
import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import {
  getTriggersService,
  createTriggerService,
  deleteTriggerService,
  editTriggerService,
  getTriggerExecutionsService,
  simulateTriggerExecutionService,
  defaultTriggerExecutionPagination,
} from '../promises';

import {actions as triggerActions} from '../duck';
import {useActions} from '../../../../utils/globalStore';

import type {
  ChatTrigger,
  ActionType,
  Pagination,
  TriggerType,
  TriggerExecution,
} from '../../../../entities';
import type {RootState} from '../../..';
import {uploadFileService} from '../../../file-storage/promises';
import {getResourceType} from '../../../file-storage/utils';
import {getFileUrl} from '../../../../utils/image_provider';
import {buildCreateTriggerPayload, buildUpdateTriggerPayload} from './adapters';
import {
  DEFAULT_TRIGGER_TYPE,
  getActionTypeForTriggerType,
  isCustomerInactivityTrigger,
} from './triggerConfig';
import type {TriggerFields} from '../components/TriggerForm/const';
import type {ListTriggersParams} from '../api';

const triggerSelector = (state: RootState) => state.chatTriggers.triggers;
const paginationSelector = (state: RootState) => state.chatTriggers.pagination;

const MULTICHAT_TRIGGER_FILE_PREFIX = 'im/multichat-triggers';

type TriggerActions = {
  actionType: ActionType,
  text?: string,
  attachments?: File[],
  images?: string[],
  videos?: string[],
  documents?: string[],
  provider?: string,
  deviceId?: string,
  templateName?: string,
  templateLang?: string,
  templateComponents?: any[],
};

export type CreateTriggerParams = TriggerFields & {
  action: TriggerActions,
};

export type EditTriggerParams = CreateTriggerParams & {
  enabled?: boolean,
};

async function uploadAttachments(
  attachments: File[] = []
): Promise<{
  images: string[],
  videos: string[],
  documents: string[],
}> {
  const images = [];
  const videos = [];
  const documents = [];

  if (!attachments.length) {
    return {images, videos, documents};
  }

  const promises = attachments.map(async file => {
    const resourceType = getResourceType(file);
    const response = await uploadFileService(file, {
      filePrefix: MULTICHAT_TRIGGER_FILE_PREFIX,
      resourceType: (resourceType: any),
    });

    return {
      id: response[0].id,
      resourceType,
    };
  });

  const result = await Promise.all(promises);

  result.forEach(attachment => {
    if (attachment.resourceType === 'IMAGE') {
      images.push(attachment.id);
    } else if (attachment.resourceType === 'VIDEO') {
      videos.push(attachment.id);
    } else {
      documents.push(attachment.id);
    }
  });

  return {images, videos, documents};
}

async function buildActionsWithUploads(
  triggerAction: TriggerActions
): Promise<any[]> {
  const actionType = triggerAction.actionType || 'SEND_MESSAGE';

  if (actionType === 'SEND_INIT_MESSAGE') {
    const provider = triggerAction.provider || 'WHATSAPP_WEB';
    const sendInitMessageConfig: any = {
      provider,
      deviceId: triggerAction.deviceId || '',
    };

    if (provider === 'WHATSAPP_API') {
      sendInitMessageConfig.whatsappApiMessage = {
        template: triggerAction.templateName || '',
        templateLang: triggerAction.templateLang || '',
        components: triggerAction.templateComponents || [],
      };
    } else {
      const uploaded = await uploadAttachments(triggerAction.attachments || []);
      sendInitMessageConfig.whatsappWebMessage = {
        text: triggerAction.text || '',
        images: uploaded.images.concat(triggerAction.images || []),
        videos: uploaded.videos.concat(triggerAction.videos || []),
        documents: uploaded.documents.concat(triggerAction.documents || []),
      };
    }

    return [
      {
        actionType: 'SEND_INIT_MESSAGE',
        sendInitMessageConfig,
      },
    ];
  }

  const uploaded = await uploadAttachments(triggerAction.attachments || []);

  return [
    {
      actionType: 'SEND_MESSAGE',
      sendMessageConfig: {
        text: triggerAction.text || '',
        images: uploaded.images.concat(triggerAction.images || []),
        videos: uploaded.videos.concat(triggerAction.videos || []),
        documents: uploaded.documents.concat(triggerAction.documents || []),
      },
    },
  ];
}

export function useChatTriggers(
  listParams?: ListTriggersParams
): {
  triggers: ChatTrigger[],
  loading: boolean,
  pagination: Pagination,
  refetch: (params?: ListTriggersParams) => void,
} {
  const [loading, setLoading] = useState(false);

  const setTriggers = useActions(triggerActions.setTriggers);
  const triggers = useSelector(triggerSelector);
  const triggerPagination = useSelector(paginationSelector);

  const fetchTriggers = (params?: ListTriggersParams) => {
    setLoading(true);

    getTriggersService(params)
      .then(({data, pagination}) => {
        if (data.length) setTriggers(data, pagination);
        else setTriggers([]);
      })
      .finally(() => setLoading(false));
  };

  const triggerTypeParam = listParams?.trigger_type;
  const nameParam = listParams?.name;
  const funnelIdParam = listParams?.funnel_id;
  const tagIdParam = listParams?.tag_id;

  useEffect(() => {
    fetchTriggers(listParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setTriggers, triggerTypeParam, nameParam, funnelIdParam, tagIdParam]);

  return {
    triggers,
    loading,
    pagination: triggerPagination,
    refetch: fetchTriggers,
  };
}

export function useChatTriggerActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const actions = useActions(triggerActions);
  const categoryPagination = useSelector(paginationSelector);

  const clearError = () => {
    setError(false);
    setErrorMessage('');
  };

  const handleMutationError = (mutationError: any) => {
    console.error(mutationError);
    setErrorMessage(
      mutationError?.error?.message ||
        mutationError?.message ||
        'Ocurrió un error al guardar el Trigger'
    );
    setError(true);
  };

  const createTrigger = async (data: CreateTriggerParams) => {
    try {
      clearError();
      setLoading(true);

      const uploadedActions = await buildActionsWithUploads({
        ...data.action,
        actionType: getActionTypeForTriggerType(
          data.triggerType || DEFAULT_TRIGGER_TYPE
        ),
      });
      const payload = buildCreateTriggerPayload({
        ...data,
        actionType: getActionTypeForTriggerType(
          data.triggerType || DEFAULT_TRIGGER_TYPE
        ),
      });
      payload.actions = uploadedActions;

      const trigger = await createTriggerService(payload);
      actions.addTrigger(trigger);
      return trigger;
    } catch (createError) {
      handleMutationError(createError);
    } finally {
      setLoading(false);
    }
  };

  const editTrigger = async (id: string, data: EditTriggerParams) => {
    try {
      clearError();
      setLoading(true);

      const uploadedActions = await buildActionsWithUploads({
        ...data.action,
        actionType: getActionTypeForTriggerType(
          data.triggerType || DEFAULT_TRIGGER_TYPE
        ),
      });
      const payload = buildUpdateTriggerPayload(
        {
          ...data,
          actionType: getActionTypeForTriggerType(
            data.triggerType || DEFAULT_TRIGGER_TYPE
          ),
        },
        data.enabled
      );
      payload.actions = uploadedActions;

      const trigger = await editTriggerService(id, payload);
      actions.editTrigger(trigger);
      return trigger;
    } catch (editError) {
      handleMutationError(editError);
    } finally {
      setLoading(false);
    }
  };

  const toggleTriggerEnabled = async (id: string, enabled: boolean) => {
    try {
      clearError();
      setLoading(true);
      const trigger = await editTriggerService(id, {enabled});
      actions.editTrigger(trigger);
      return trigger;
    } catch (toggleError) {
      handleMutationError(toggleError);
    } finally {
      setLoading(false);
    }
  };

  const deleteTrigger = async (id: string) => {
    try {
      clearError();
      setLoading(true);
      await deleteTriggerService(id);
      actions.deleteTrigger(id);
    } catch (deleteError) {
      handleMutationError(deleteError);
    } finally {
      setLoading(false);
    }
  };

  const changeTriggerPagination = (
    page: number,
    filters?: ListTriggersParams
  ) => {
    const paginationRequest = {
      page: page - 1,
      size: categoryPagination.size,
      ...filters,
    };

    getTriggersService(paginationRequest).then(({data, pagination}) => {
      if (data.length) actions.setTriggers(data, pagination);
    });
    actions.setPaginationPage(page);
  };

  return {
    error,
    errorMessage,
    loading,
    clearError,
    editTrigger,
    createTrigger,
    deleteTrigger,
    toggleTriggerEnabled,
    changeTriggerPagination,
  };
}

export function getTriggerLabel(triggerPattern: string) {
  if (triggerPattern === 'MATCHES_WITH') return 'Sea:';
  if (triggerPattern === 'ENDS_WITH') return 'Termine en:';
  if (triggerPattern === 'BEGINS_WITH') return 'Empieza en:';
  return '';
}

export function getTriggerTypeLabel(triggerType: TriggerType) {
  if (isCustomerInactivityTrigger(triggerType)) {
    return 'Inactividad del cliente';
  }
  return 'Mensaje recibido';
}

export function getTriggerAttachmentUrl(urlOrId: string) {
  if (urlOrId.startsWith('https')) {
    return urlOrId;
  }
  return getFileUrl(urlOrId);
}

export function getTriggerExecutionStateLabel(state: string) {
  if (state === 'SUCCEEDED') return 'Exitoso';
  if (state === 'FAILED') return 'Fallido';
  if (state === 'PENDING') return 'Pendiente';
  if (state === 'RUNNING') return 'En curso';
  return state || '—';
}

type SimulateHookOptions = {|
  enabled?: boolean,
|};

export function useTriggerExecutionSimulate(
  triggerId: ?string,
  options?: SimulateHookOptions = {}
): {
  estimatedCount: ?number,
  loading: boolean,
  error: boolean,
} {
  const {enabled = true} = options;
  const [estimatedCount, setEstimatedCount] = useState<?number>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled || !triggerId) {
      setEstimatedCount(null);
      setLoading(false);
      setError(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    simulateTriggerExecutionService(triggerId)
      .then(response => {
        if (cancelled) return;
        setEstimatedCount(response.estimatedConversationsCount);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setEstimatedCount(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, triggerId]);

  return {estimatedCount, loading, error};
}

type ExecutionsHookOptions = {|
  enabled?: boolean,
  size?: number,
|};

export function useTriggerExecutions(
  triggerId: ?string,
  options?: ExecutionsHookOptions = {}
): {
  executions: TriggerExecution[],
  loading: boolean,
  pagination: Pagination,
  page: number,
  setPage: (page: number) => void,
} {
  const {
    enabled = true,
    size = defaultTriggerExecutionPagination.size,
  } = options;
  const [executions, setExecutions] = useState<TriggerExecution[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pagination, setPagination] = useState<Pagination>({
    page: 0,
    size,
    total: 0,
  });

  useEffect(() => {
    setPage(0);
  }, [triggerId]);

  useEffect(() => {
    if (!enabled || !triggerId) {
      setExecutions([]);
      setPagination({page: 0, size, total: 0});
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    getTriggerExecutionsService(triggerId, {page, size})
      .then(response => {
        if (cancelled) return;
        setExecutions(response.data || []);
        setPagination(
          response.pagination || {
            page,
            size,
            total: (response.data || []).length,
          }
        );
      })
      .catch(() => {
        if (cancelled) return;
        setExecutions([]);
        setPagination({page, size, total: 0});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, triggerId, page, size]);

  return {
    executions,
    loading,
    pagination,
    page,
    setPage,
  };
}
